import React, { useEffect, useRef, useState } from "react";

// MODULES
import * as d3 from "d3";

// STYLES
import "./StaticForwardingPPGWaveformLineChart.scss";

interface StaticForwardingPPGWaveformLineChartProps {
    className?: string;
    data: number[];
    containerWidth: number;
    inPrintMode?: boolean;
    pixelsPerSecond?: number; // not to be confused with sampling - this is literally the length of a second on the screen
    chartHeight?: number;
    verticalGridLines?: number;
    lineStrokeWidth?: number;
}

export const StaticForwardingPPGWaveformLineChart2: React.FC<
    StaticForwardingPPGWaveformLineChartProps
> = ({
    className = "",
    data,
    containerWidth,
    inPrintMode = false,
    pixelsPerSecond = 100,
    verticalGridLines = 3,
    chartHeight = 200,
    lineStrokeWidth = 4,
}) => {
    const [widthToUse, setWidthToUse] = useState<number>(
        containerWidth > 0 ? containerWidth : 750,
    );
    const containerRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (containerWidth > 0) setWidthToUse(containerWidth);
    }, [containerWidth]);

    useEffect(() => {
        if (!data.length || !widthToUse) return;

        const container = d3.select(containerRef.current);
        container.selectAll("*").remove();

        const samplesPerSecond = 30; // 30fps camera
        const samplesPerTick = samplesPerSecond;

        const totalTicks = Math.max(1, Math.ceil(data.length / samplesPerTick));
        // In print mode compress the time scale so the entire waveform fits
        // on a single row at the available print width. Otherwise use the
        // requested fixed pixels-per-tick.
        const pixelsPerTick = inPrintMode
            ? Math.max(8, (widthToUse - 30) / totalTicks)
            : pixelsPerSecond;

        // NOTE - if you change the  *...Report page .contentWrapper class margin it needs to be factored
        // in here to prevent the graph from overflowing the horizontal bounds of the parent container when
        // the graph needs to recalc ticks per row
        const ticksPerRow = Math.floor((widthToUse - 30) / pixelsPerTick);
        const samplesPerRow = ticksPerRow * samplesPerTick;

        // Cleave data so it ends on a major tick boundary
        const maxFullTicks = Math.floor(data.length / samplesPerTick);
        const maxUsableSamples = maxFullTicks * samplesPerTick;
        const trimmedData = data.slice(0, maxUsableSamples);

        //break trimmed data into chunks for each row
        const chunks: number[][] = [];
        for (let i = 0; i < trimmedData.length; i += samplesPerRow) {
            const chunk = trimmedData.slice(i, i + samplesPerRow);

            // Duplicate last sample from previous row for continuity
            if (i !== 0 && trimmedData[i - 1] !== undefined) {
                chunk.unshift(trimmedData[i - 1]);
            }

            chunks.push(chunk);
        }

        chunks.forEach((chunk, rowIndex) => {
            //const isLastChunk = rowIndex === chunks.length - 1;
            const actualTicks = Math.ceil((chunk.length - 1) / samplesPerTick);
            const width = actualTicks * pixelsPerTick;
            const height = chartHeight;
            const margin = { top: 10, right: 10, bottom: 24, left: 10 };
            const innerWidth = width - margin.left - margin.right;
            const innerHeight = height - margin.top - margin.bottom;

            const svg = container
                .append("svg")
                .attr("width", width)
                .attr("height", height);

            const ppgGraph = svg
                .append("g")
                .attr("transform", `translate(${margin.left},${margin.top})`);

            const xScale = d3
                .scaleLinear()
                .domain([0, chunk.length - 1])
                .range([0, innerWidth]);

            const yExtent = d3.extent(chunk);
            const yScale = d3
                .scaleLinear()
                .domain(yExtent as [number, number])
                .range([innerHeight, 0]);

            const lineGenerator = d3
                .line<number>()
                .x((_, i) => xScale(i))
                .y((d) => yScale(d))
                .curve(d3.curveLinear);

            const offset = rowIndex * ticksPerRow;
            const majorTicks = d3.range(
                rowIndex === 0 ? 0 : 1,
                chunk.length - 1 + verticalGridLines,
                samplesPerTick,
            );

            // Major vertical grid ticks
            majorTicks.forEach((tick, idx) => {
                ppgGraph
                    .append("line")
                    .attr("x1", xScale(tick))
                    .attr("y1", 0)
                    .attr("x2", xScale(tick))
                    .attr("y2", innerHeight)
                    .attr("stroke", "#ccc")
                    .attr("stroke-width", 1);
            });

            // Horizontal grid lines
            const totalVerticalSegments = (actualTicks - 1) * verticalGridLines;
            const squareSize =
                innerWidth / (totalVerticalSegments + actualTicks - 1);
            const numHorizontalLines = Math.floor(innerHeight / squareSize);

            for (let i = 0; i <= numHorizontalLines; i++) {
                const y = innerHeight - i * squareSize;
                ppgGraph
                    .append("line")
                    .attr("x1", 0)
                    .attr("y1", isNaN(y) ? 0 : y) // Sanity null check
                    .attr("x2", innerWidth)
                    .attr("y2", isNaN(y) ? 0 : y) // Sanity null check
                    .attr("stroke", "#ccc")
                    .attr("stroke-width", 1);
            }

            // Minor vertical grid lines between major ticks
            for (let i = 0; i < majorTicks.length - 1; i++) {
                const start = majorTicks[i];
                const end = majorTicks[i + 1];
                const step = (end - start) / (verticalGridLines + 1);

                for (let j = 1; j <= verticalGridLines; j++) {
                    const minorTick = start + step * j;
                    ppgGraph
                        .append("line")
                        .attr("x1", xScale(minorTick))
                        .attr("y1", 0)
                        .attr("x2", xScale(minorTick))
                        .attr("y2", innerHeight)
                        .attr("stroke", "#ccc")
                        .attr("stroke-width", 1);
                }
            }

            // X-axis labels: keep grid dense (every second) but only label
            // every Nth tick so the labels never overlap. Stride is picked
            // from the current pixels-per-second so the chart stays readable
            // when the time scale is compressed for print.
            const MIN_LABEL_PX = 28;
            const labelStride = Math.max(
                1,
                Math.ceil(MIN_LABEL_PX / pixelsPerTick),
            );
            const labelTicks = majorTicks.filter(
                (_, i) => i % labelStride === 0,
            );

            const xAxis = d3
                .axisBottom(xScale)
                .tickValues(labelTicks)
                .tickFormat(
                    (d) =>
                        `${(offset + d.valueOf() / samplesPerSecond).toFixed(
                            0,
                        )}s`,
                )
                .tickSize(6)
                .tickPadding(4);

            ppgGraph
                .append("g")
                .attr("transform", `translate(0, ${innerHeight})`)
                .call(xAxis)
                .call((g) => g.select(".domain").attr("stroke", "#000"))
                .call((g) =>
                    g
                        .selectAll("text")
                        .attr("font-size", 10)
                        .attr("fill", "#555"),
                );

            // Waveform
            ppgGraph
                .append("path")
                .datum(chunk)
                .attr("fill", "none")
                .attr("stroke", "#000")
                .attr("stroke-width", lineStrokeWidth)
                .attr("d", lineGenerator);
        });
    }, [
        data,
        widthToUse,
        inPrintMode,
        pixelsPerSecond,
        chartHeight,
        verticalGridLines,
        lineStrokeWidth,
    ]);

    return (
        <div
            ref={containerRef}
            className="StaticForwardingPPGWaveformLineChart"
        />
    );
};

export default StaticForwardingPPGWaveformLineChart2;
